import type { components } from '@fluencelabs/vodopad-admin-api'
import { useCustom } from '@refinedev/core'
import { Alert, Table, Tag, Typography } from 'antd'

import { FormattedDate } from '@/components/common/FormattedDate'
import { MonoId } from '@/components/common/MonoId'
import { StatusTag } from '@/components/common/StatusTag'
import { VODOPAD_ENDPOINT } from '@/constants/constants'
import type { PaginatedItems } from '@/types/api'

type NotificationDto = components['schemas']['AdminNotificationDto']

const STATUS_COLORS = {
  Sent: 'green',
  Pending: 'orange',
  Failed: 'red',
  Skipped: 'default',
}

export function UserNotificationsTab({ userId }: { userId: string }) {
  const notificationsQuery = useCustom<PaginatedItems<NotificationDto>>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/users/${userId}/notifications`,
    method: 'get',
    config: { query: { page: 1, perPage: 50 } },
  })

  const notifications =
    (
      notificationsQuery.query.data?.data as
        | PaginatedItems<NotificationDto>
        | undefined
    )?.items ?? []

  if (notificationsQuery.query.isError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load notifications"
        description={notificationsQuery.query.error?.message}
      />
    )
  }

  return (
    <Table
      loading={notificationsQuery.query.isLoading}
      dataSource={notifications}
      rowKey="id"
      pagination={{ pageSize: 10 }}
      columns={[
        {
          title: 'Type',
          dataIndex: 'notification_type',
          key: 'notification_type',
          width: 90,
          render: (type: string) => <Tag>{type}</Tag>,
        },
        {
          title: 'Recipient',
          dataIndex: 'recipient',
          key: 'recipient',
          ellipsis: true,
          render: (recipient: string) => (
            <Typography.Text copyable={{ text: recipient }}>
              {recipient}
            </Typography.Text>
          ),
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          width: 100,
          render: (status: string) => (
            <StatusTag status={status} colorMap={STATUS_COLORS} />
          ),
        },
        {
          title: 'Template',
          dataIndex: 'sendgrid_template_id',
          key: 'sendgrid_template_id',
          width: 160,
          render: (id: string) => <MonoId id={id} />,
        },
        {
          title: 'Sent',
          dataIndex: 'sent_at',
          key: 'sent_at',
          width: 180,
          render: (date: string | null) => (
            <FormattedDate date={date} fmt="PPp" />
          ),
        },
        {
          title: 'Created',
          dataIndex: 'created_at',
          key: 'created_at',
          width: 180,
          render: (date: string) => <FormattedDate date={date} fmt="PPp" />,
        },
        {
          title: 'Error',
          dataIndex: 'error_message',
          key: 'error_message',
          ellipsis: true,
          render: (error: string | null) =>
            error ? (
              <Typography.Text type="danger" ellipsis={{ tooltip: error }}>
                {error}
              </Typography.Text>
            ) : (
              '—'
            ),
        },
      ]}
    />
  )
}
